import { useRef, useState } from 'react'
import type { FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { cn } from '@/lib/cn'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Sheet } from '@/components/ui/Sheet'
import { SkeletonRows } from '@/components/ui/Skeleton'
import { Textarea } from '@/components/ui/Textarea'
import { useProfile } from '@/auth/SessionProvider'
import { confirmPassword } from '@/data/auth'
import { MIN_REASON, deleteRecord, previewDelete } from '@/data/ownerDelete'
import type { DeletableTable } from '@/data/ownerDelete'

const errText = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong. Please try again.')

/**
 * "Delete" for the owner of the app, and for nobody else: it renders nothing for anyone who is not the
 * owner. (The database is what really refuses everyone else; this just keeps the button out of sight.)
 */
export function OwnerDeleteButton({
  table,
  id,
  what,
  onDeleted,
  className,
  wrap,
}: {
  table: DeletableTable
  id: string
  /** What it is, for the button's accessible name: "payment", "member"... */
  what: string
  onDeleted?: () => void
  className?: string
  /** Classes for a wrapper around the button. The wrapper exists ONLY for the owner, so for everyone else the screen is exactly as it was. */
  wrap?: string
}) {
  const profile = useProfile()
  const [open, setOpen] = useState(false)
  if (!profile.isOwner) return null
  const button = (
    <>
      {/* Quiet on purpose (red text, no box): it sits next to money and only the owner sees it. The
          confirmation is the sheet it opens: what goes, a fresh password, a reason and the word DELETE. */}
      <Button type="button" variant="ghost" size="sm" className={cn('text-bad hover:bg-bad-tint', className)} aria-label={`Delete this ${what} permanently`} onClick={() => setOpen(true)}>
        Delete
      </Button>
      {open && <OwnerDeleteSheet table={table} id={id} onClose={() => setOpen(false)} onDeleted={onDeleted} />}
    </>
  )
  return wrap ? <div className={wrap}>{button}</div> : button
}

/** Look, then confirm: what goes, what stays, then password + reason + the word DELETE. */
export function OwnerDeleteSheet({
  table,
  id,
  onClose,
  onDeleted,
}: {
  table: DeletableTable
  id: string
  onClose: () => void
  onDeleted?: () => void
}) {
  const qc = useQueryClient()
  const preview = useQuery({
    queryKey: ['owner-delete-preview', table, id],
    queryFn: () => previewDelete(table, id),
    // Always look afresh: the fingerprint must match what is true at the moment of deleting.
    gcTime: 0,
    staleTime: 0,
    retry: false,
  })
  const [password, setPassword] = useState('')
  const [reason, setReason] = useState('')
  const [word, setWord] = useState('')
  const [error, setError] = useState('')
  const [done, setDone] = useState<string | null>(null)
  const busy = useRef(false)

  const run = useMutation({
    mutationFn: async () => {
      if (!preview.data) throw new Error('Wait for the list of what will be deleted.')
      await confirmPassword(password)
      return deleteRecord({ table, id, reason, confirmWord: word, planHash: preview.data.planHash })
    },
  })

  const ready = Boolean(preview.data) && password.length > 0 && reason.trim().length >= MIN_REASON && word === 'DELETE'

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (busy.current || !ready) return
    setError('')
    busy.current = true
    try {
      const r = await run.mutateAsync()
      // Do NOT refresh the screen yet: the row this sheet belongs to would disappear, taking the sheet and
      // its confirmation with it. The refresh (and any navigation away) happens when the sheet is closed.
      setPassword('')
      setDone(`Deleted: ${r.label}.`)
    } catch (err) {
      setError(errText(err))
      // If things changed since the owner looked, show the new list.
      if (/changed since you looked/i.test(errText(err))) void preview.refetch()
    } finally {
      busy.current = false
    }
  }

  /** Closing after a successful delete: now refresh everything, and leave the page if it was this record's own. */
  const finish = () => {
    if (!done) return onClose()
    void qc.invalidateQueries()
    onClose()
    onDeleted?.()
  }

  return (
    <Sheet isOpen onClose={finish} title="Delete for good">
      {done ? (
        <div className="flex flex-col gap-4">
          <p role="status" className="text-ink">{done}</p>
          <p className="text-body text-muted">It is gone from every screen and total. A permanent entry in the deletion log records who deleted it, when and why.</p>
          <Button size="lg" onClick={finish}>Close</Button>
        </div>
      ) : (
        <form onSubmit={(e) => void submit(e)} className="flex flex-col gap-4" noValidate>
          {preview.isPending ? (
            <SkeletonRows rows={4} />
          ) : preview.isError ? (
            <div>
              <p role="alert" className="text-body text-bad">{errText(preview.error)}</p>
              <Button type="button" variant="secondary" size="lg" className="mt-3" onClick={onClose}>Close</Button>
            </div>
          ) : (
            <>
              <div>
                <p className="text-section font-semibold text-ink">{preview.data.label}</p>
                <p className="mt-1 text-neutral">
                  This is permanent. It cannot be undone from the app. Deleting it also does the following.
                </p>
              </div>
              <ul className="flex flex-col gap-1 rounded-control border border-bad/30 bg-bad-tint p-3 text-body text-bad" aria-label="What will be deleted">
                {preview.data.items.filter((i) => i.action === 'delete').map((i) => (
                  <li key={`d-${i.table}`}>Removes {i.text}{i.firstOn ? (i.lastOn && i.lastOn !== i.firstOn ? ` (${i.firstOn} to ${i.lastOn})` : ` (${i.firstOn})`) : ''}</li>
                ))}
              </ul>
              {preview.data.items.some((i) => i.action === 'detach') && (
                <ul className="flex flex-col gap-1 rounded-control border border-line bg-page p-3 text-body text-ink" aria-label="What will stay">
                  {preview.data.items.filter((i) => i.action === 'detach').map((i) => (
                    <li key={`x-${i.table}-${i.col}`}>Keeps {i.text}</li>
                  ))}
                </ul>
              )}
              {preview.data.departments.length > 0 && (
                <p className="text-body text-neutral">Departments affected: {preview.data.departments.join(', ')}.</p>
              )}
              {preview.data.warnings.map((w) => (
                <p key={w} role="alert" className="rounded-control border border-wait/40 bg-wait-tint p-3 text-body text-wait">{w}</p>
              ))}

              <Textarea
                label="Why is it being deleted?"
                hint={`At least ${MIN_REASON} characters. This is kept in the deletion log for everyone who audits the books.`}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
              />
              <Input label="Your password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
              <Input label="Type DELETE to confirm" autoComplete="off" value={word} onChange={(e) => setWord(e.target.value)} />
              {error && <p role="alert" className="text-body text-bad">{error}</p>}
              <div className="flex flex-wrap gap-2">
                <Button type="submit" variant="danger" size="lg" disabled={!ready} isLoading={run.isPending}>
                  {run.isPending ? 'Deleting...' : 'Delete permanently'}
                </Button>
                <Button type="button" variant="secondary" size="lg" onClick={onClose}>Cancel</Button>
              </div>
            </>
          )}
        </form>
      )}
    </Sheet>
  )
}
